import React, { createContext, useContext, useState, ReactNode, useEffect, useLayoutEffect } from "react";

interface PageHeaderInfo {
  title: string;
  subtitle?: string;
  icon?: React.ReactNode;
  onBack?: () => void;
  chip?: {
    label: string;
    icon?: React.ReactNode;
    /** Show an animated wave indicator in place of the count while loading. */
    loading?: boolean;
  };
}

export interface SubNavItem {
  label: string;
  path: string;
  icon?: React.ReactNode;
}

interface LayoutContextType {
  title: string;
  setTitle: (title: string) => void;
  pageHeader: PageHeaderInfo | null;
  setPageHeader: (header: PageHeaderInfo | null, setterId?: string) => void;
  /** The app's main navigation sidebar (left rail). Distinct from
   *  sidePanelOpen, which is the chat/AI side panel. */
  sidebarOpen: boolean;
  setSidebarOpen: React.Dispatch<React.SetStateAction<boolean>>;
  sidePanelOpen: boolean;
  setSidePanelOpen: (open: boolean) => void;
  isChatAvailable: boolean;
  setChatAvailable: (available: boolean) => void;
  subNavItems: SubNavItem[];
  setSubNavItems: (items: SubNavItem[]) => void;
}

const LayoutContext = createContext<LayoutContextType | undefined>(undefined);

export const LayoutProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [title, setTitle] = useState("My Reports");
  const [pageHeader, setPageHeaderState] = useState<PageHeaderInfo | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidePanelOpen, setSidePanelOpen] = useState(false);
  const [isChatAvailable, setChatAvailable] = useState(false);
  const [subNavItems, setSubNavItems] = useState<SubNavItem[]>([]);
  const activeSetterIdRef = React.useRef<string | null>(null);

  const setPageHeader = React.useCallback((header: PageHeaderInfo | null, setterId?: string) => {
    if (header) {
      if (setterId) {
        activeSetterIdRef.current = setterId;
      }
      setPageHeaderState(header);
    } else {
      // Only clear if the requester is the current owner
      if (setterId && setterId === activeSetterIdRef.current) {
        setPageHeaderState(null);
        activeSetterIdRef.current = null;
      } else if (!setterId) {
        // Force clear if no ID provided
        setPageHeaderState(null);
        activeSetterIdRef.current = null;
      }
    }
  }, []);

  return (
    <LayoutContext.Provider value={{ title, setTitle, pageHeader, setPageHeader, sidebarOpen, setSidebarOpen, sidePanelOpen, setSidePanelOpen, isChatAvailable, setChatAvailable, subNavItems, setSubNavItems }}>
      {children}
    </LayoutContext.Provider>
  );
};

export const useLayout = () => {
  const context = useContext(LayoutContext);
  if (!context) {
    throw new Error("useLayout must be used within a LayoutProvider");
  }
  return context;
};

export const usePageTitle = (title: string) => {
  const { setTitle } = useLayout();
  useEffect(() => {
    setTitle(title);
  }, [title, setTitle]);
};

export const usePageHeader = (header: PageHeaderInfo) => {
  const { setTitle, setPageHeader } = useLayout();

  // Generate a unique ID for this component instance
  const setterIdRef = React.useRef(Math.random().toString(36).substr(2, 9));

  // Store header in a ref to avoid dependency issues
  const headerRef = React.useRef(header);
  headerRef.current = header;

  // Effect 1: Cleanup on unmount only
  useLayoutEffect(() => {
    return () => {
      setPageHeader(null, setterIdRef.current);
    };
  }, [setPageHeader]);

  // Effect 2: Update header on changes (and initial mount)
  useLayoutEffect(() => {
    setTitle(headerRef.current.title);
    setPageHeader(headerRef.current, setterIdRef.current);
  }, [header.title, header.subtitle, header.chip?.label, header.chip?.loading, setTitle, setPageHeader]);
};

export const useSubNav = (items: SubNavItem[]) => {
  const { setSubNavItems } = useLayout();

  const itemsRef = React.useRef(items);
  itemsRef.current = items;

  // Re-register when the item CONTENT changes, not on every render. Callers
  // pass a fresh array each render (e.g. a flag-conditional tab list), so a
  // reference dep would thrash; a stable content key means we re-register
  // only when a tab is actually added, removed, or renamed - which is what
  // lets a feature-flagged tab appear once its flag resolves after auth
  // restore.
  const contentKey = items.map((item) => `${item.label}|${item.path}`).join(",");

  useLayoutEffect(() => {
    setSubNavItems(itemsRef.current);
    return () => {
      setSubNavItems([]);
    };
  }, [setSubNavItems, contentKey]);
};
